import React, { useEffect, useState, useRef } from "react";
import * as Icon from "react-bootstrap-icons";
import axios from "axios";
import { API_URL } from "../../config/constant";
import checkPDFPasswordProtected from "../../utilities/checkPDFPasswordProtected";
import Spinner from "react-bootstrap/Spinner";

// Move this outside the component to comply with React Hooks rules
const maxBankStatements = 4; // Arbitrary limit to prevent excessive refs
const bankStatementRefs = Array.from({ length: maxBankStatements }, () =>
  React.createRef()
);

export default function SalariedDocument({
  activeCard,
  preFillData,
  userType,
  handleCongratulation,
  caseId,
  loading,
}) {
  const [files, setFiles] = useState({
    panCard: { file: [], name: "", type: "", error: false, errorMsg: "" },
    aadhaarFront: { file: [], name: "", type: "", error: false, errorMsg: "" },
    aadhaarBack: {
      file: [],
      name: "",
      type: "",
      error: false,
      errorMsg: "",
      passwordProtected: false,
      password: "",
      passwordError: false,
    },
    salarySlip1: { file: [], name: "", type: "", error: false, errorMsg: "" },
    salarySlip2: { file: [], name: "", type: "", error: false, errorMsg: "" },
    salarySlip3: { file: [], name: "", type: "", error: false, errorMsg: "" },
    bankStatements: [
      {
        file: [],
        name: "",
        type: "",
        error: false,
        errorMsg: "",
        passwordProtected: false,
        password: "",
        passwordError: false,
      },
    ],
    formSixteen: { file: [], name: "", type: "", error: false, errorMsg: "" },
  });
  const [isDisableBtn, setIsDisableBtn] = useState(false);
  const [display, setDisplay] = useState("none");
  const [fileData, setFileData] = useState(false);

  // Move all useRef calls to the top level
  const fileRefs = {
    panCard: useRef(),
    aadhaarFront: useRef(),
    aadhaarBack: useRef(),
    salarySlip1: useRef(),
    salarySlip2: useRef(),
    salarySlip3: useRef(),
    bankStatements: bankStatementRefs,
    formSixteen: useRef(),
  };

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
  ];
  const allowedPdfTypes = ["application/pdf"];
  const MAX_FILE_SIZE_IMAGE = 2048; // 2MB
  const MAX_FILE_SIZE_PDF = 5120; // 5MB

  const addBankStatement = () => {
    if (files.bankStatements.length >= maxBankStatements) return; // Prevent exceeding max refs
    setFiles((prev) => ({
      ...prev,
      bankStatements: [
        ...prev.bankStatements,
        {
          file: [],
          name: "",
          type: "",
          error: false,
          errorMsg: "",
          passwordProtected: false,
          password: "",
          passwordError: false,
        },
      ],
    }));
  };

  const handleFileChange = async (key, index, e) => {
    const selectedFile = e.target.files;
    if (!selectedFile.length) return;

    const file = selectedFile[0];
    const isPdf = file.type === "application/pdf";
    const sizeLimit = isPdf ? MAX_FILE_SIZE_PDF : MAX_FILE_SIZE_IMAGE;
    const validTypes = isPdf ? allowedPdfTypes : allowedTypes;

    let error = false,
      errorMsg = "";
    if (!validTypes.includes(file.type)) {
      error = true;
      errorMsg = `Only ${isPdf ? "PDF" : "JPEG, PNG, GIF, PDF"} allowed.`;
    } else if (file.size / 1024 > sizeLimit) {
      error = true;
      errorMsg = `File size exceeds ${sizeLimit / 1024}MB.`;
    }

    let passwordProtected = false;
    if (isPdf && (key === "aadhaarFront" || key === "bankStatements")) {
      passwordProtected = await checkPDFPasswordProtected(file);
    }

    setFiles((prev) => {
      if (key === "bankStatements") {
        const updatedBankStatements = [...prev.bankStatements];
        updatedBankStatements[index] = {
          file: [file],
          name: file.name,
          type: isPdf ? "pdf" : file.type.split("/")[1],
          error,
          errorMsg,
          passwordProtected,
          password: passwordProtected
            ? prev.bankStatements[index]?.password || ""
            : "",
          passwordError: false,
        };
        return { ...prev, bankStatements: updatedBankStatements };
      }
      return {
        ...prev,
        [key]: {
          file: [file],
          name: file.name,
          type: isPdf ? "pdf" : file.type.split("/")[1],
          error,
          errorMsg,
          passwordProtected,
          password: passwordProtected ? prev[key]?.password || "" : "",
          passwordError: false,
        },
      };
    });
  };

  const handlePasswordChange = (key, index, value) => {
    setFiles((prev) => {
      if (key === "bankStatements") {
        const updatedBankStatements = [...prev.bankStatements];
        updatedBankStatements[index] = {
          ...updatedBankStatements[index],
          password: value,
          passwordError: false,
        };
        return { ...prev, bankStatements: updatedBankStatements };
      }
      return {
        ...prev,
        [key]: { ...prev[key], password: value, passwordError: false },
      };
    });
  };

  const removeFile = (key, index) => {
    if (key === "bankStatements" && files.bankStatements.length === 1) return; // Prevent removing the last bank statement
    setFiles((prev) => {
      if (key === "bankStatements") {
        const updatedBankStatements = prev.bankStatements.filter(
          (_, i) => i !== index
        );
        return { ...prev, bankStatements: updatedBankStatements };
      }
      return {
        ...prev,
        [key]: {
          file: [],
          name: "",
          type: "",
          error: false,
          errorMsg: "",
          passwordProtected: false,
          password: "",
          passwordError: false,
        },
      };
    });
    if (key !== "bankStatements") fileRefs[key].current.value = null;
    else fileRefs.bankStatements[index].current.value = null;
  };

  const handleValidation = () => {
    const requiredFields = [
      "panCard",
      "aadhaarFront",
      ...(files.aadhaarFront.passwordProtected ? [] : ["aadhaarBack"]),
      "salarySlip1",
      "salarySlip2",
      "salarySlip3",
      "formSixteen",
    ];
    let isValid = true;

    requiredFields.forEach((key) => {
      if (!files[key].file.length || files[key].errorMsg) {
        setFiles((prev) => ({ ...prev, [key]: { ...prev[key], error: true } }));
        isValid = false;
      }
      if (files[key].passwordProtected && !files[key].password) {
        setFiles((prev) => ({
          ...prev,
          [key]: { ...prev[key], passwordError: true },
        }));
        isValid = false;
      }
    });

    const validBankStatements = files.bankStatements.some(
      ({ file, errorMsg }) => file.length && !errorMsg
    );
    if (!validBankStatements) {
      setFiles((prev) => ({
        ...prev,
        bankStatements: prev.bankStatements.map((stmt) => ({
          ...stmt,
          error: true,
        })),
      }));
      isValid = false;
    }
    files.bankStatements.forEach((stmt, index) => {
      if (stmt.passwordProtected && !stmt.password) {
        setFiles((prev) => {
          const updatedBankStatements = [...prev.bankStatements];
          updatedBankStatements[index] = {
            ...updatedBankStatements[index],
            passwordError: true,
          };
          return { ...prev, bankStatements: updatedBankStatements };
        });
        isValid = false;
      }
    });

    return isValid;
  };

  const handleUploadDocument = async () => {
    if (fileData) {
      handleCongratulation({
        name: preFillData?.personal_detail?.name,
        case_number: preFillData?.user_detail?.case_number,
      });
      return;
    }

    if (!handleValidation()) return;

    setDisplay("flex");
    setIsDisableBtn(true);

    try {
      const {
        data: { token },
      } = await axios.post(`${API_URL}token/generate-token`, {
        user_id: localStorage.getItem("user_id"),
      });

      const formData = new FormData();
      formData.append("case_id", caseId);
      formData.append("token", token);
      Object.entries(files).forEach(([key, value]) => {
        if (key === "bankStatements") {
          value.forEach(({ file, password }, index) => {
            if (file.length) {
              formData.append("bank_statement", file[0]);
              formData.append(
                `bank_statement_${index}_password`,
                password || ""
              );
            }
          });
        } else if (value.file.length) {
          const field =
            key === "salarySlip1" ||
            key === "salarySlip2" ||
            key === "salarySlip3"
              ? "salary_slip"
              : key === "formSixteen"
              ? "itr"
              : key.replace(/([A-Z])/g, "_$1").toLowerCase();
          formData.append(field, value.file[0]);
          if (value.password && field === "aadhaar_front")
            formData.append("aadhaar_password", value.password);
          else if (value.password)
            formData.append(`${field}_password`, value.password);
        }
      });

      const { data: response } = await axios.post(
        `${API_URL}files/upload`,
        formData
      );
      if (response?.status === 200) handleCongratulation(response?.data);
    } catch (e) {
      console.error(e);
    } finally {
      setDisplay("none");
      setIsDisableBtn(false);
    }
  };

  useEffect(() => {
    if (Object.keys(preFillData).length) {
      setFileData(!!preFillData?.files.length);
      const fileMap = {
        pan_card: "panCard",
        aadhaar_front: "aadhaarFront",
        aadhaar_back: "aadhaarBack",
        salary_slip: "salarySlip",
        itr: "formSixteen",
      };
      const newFiles = { ...files };
      const salaryFiles = [];
      const bankStatementFiles = [];
      const itrFiles = [];

      preFillData?.files.forEach(({ document_type, filename }) => {
        if (document_type === "salary_slip") salaryFiles.push(filename);
        else if (document_type === "bank_statement")
          bankStatementFiles.push(filename);
        else if (document_type === "itr") itrFiles.push(filename);
        else if (fileMap[document_type]) {
          newFiles[fileMap[document_type]] = {
            ...newFiles[fileMap[document_type]],
            name: filename,
          };
        }
      });

      if (salaryFiles.length) {
        ["salarySlip1", "salarySlip2", "salarySlip3"].forEach((key, i) => {
          if (salaryFiles[i])
            newFiles[key] = { ...newFiles[key], name: salaryFiles[i] };
        });
      }
      if (bankStatementFiles.length) {
        newFiles.bankStatements = bankStatementFiles.map((filename, index) => ({
          file: [],
          name: filename,
          type: "",
          error: false,
          errorMsg: "",
          passwordProtected: false,
          password: "",
          passwordError: false,
        }));
      }
      setFiles(newFiles);
    }
  }, [preFillData]);

  useEffect(() => loading(display), [display]);

  const startAndEnd = (str) =>
    str.length > 35 ? `${str.slice(0, 20)}...${str.slice(-10)}` : str;

  const renderFileUpload = (key, label, accept, isPdfOnly = false, index) => {
    const {
      file,
      name,
      type,
      error,
      errorMsg,
      passwordProtected,
      password,
      passwordError,
    } = key === "bankStatements" ? files.bankStatements[index] : files[key];
    return (
      <>
        <div className="uploadfilesArea" key={`${key}-${index}`}>
          <div className="filnameIcons">
            <div className="fileIc">
              <span className="imgWithName">
                <span className="imgIcons">
                  {type === "pdf" ? (
                    <Icon.FiletypePdf
                      color={error ? "#f62b2b" : "#1962CD"}
                      size={50}
                    />
                  ) : type === "png" ? (
                    <Icon.FiletypePng
                      color={error ? "#f62b2b" : "#1962CD"}
                      size={50}
                    />
                  ) : type === "jpg" || type === "jpeg" ? (
                    <Icon.FiletypeJpg
                      color={error ? "#f62b2b" : "#1962CD"}
                      size={50}
                    />
                  ) : type === "gif" ? (
                    <Icon.FiletypeGif
                      color={error ? "#f62b2b" : "#1962CD"}
                      size={50}
                    />
                  ) : (
                    <Icon.Files
                      color={error ? "#f62b2b" : "#1962CD"}
                      size={50}
                    />
                  )}
                </span>
                <span className="imageName">
                  <span
                    title={name}
                    className={error ? "vrtext error" : "vrtext"}
                  >
                    {file.length ? (
                      <>
                        {label}
                        <br />
                        {startAndEnd(name)}
                      </>
                    ) : name ? (
                      name
                    ) : (
                      label
                    )}
                  </span>
                  {!file.length && !name && (
                    <span className="vrtext message">
                      * File Format: {isPdfOnly ? "PDF" : "JPEG, PNG, GIF, PDF"}
                      ; Max size: {isPdfOnly ? "5MB" : "2MB"}
                    </span>
                  )}
                  {errorMsg && (
                    <span className="vrtext message">{errorMsg}</span>
                  )}
                </span>
              </span>
              {file.length ? (
                <span
                  className="greenTick"
                  onClick={() => removeFile(key, index)}
                >
                  <i className="fa fa-check"></i>
                </span>
              ) : (
                ""
              )}
            </div>
          </div>
          <div
            className="uploadbtnSize"
            style={{ display: "flex", alignItems: "center" }}
          >
            <input
              type="file"
              accept={
                isPdfOnly
                  ? "application/pdf"
                  : "image/png,image/jpeg,image/gif,application/pdf"
              }
              onChange={(e) => handleFileChange(key, index, e)}
              style={{ display: "none" }}
              ref={
                key === "bankStatements"
                  ? fileRefs.bankStatements[index]
                  : fileRefs[key]
              }
            />
            <div
              className="uplodBtns"
              onClick={() =>
                (key === "bankStatements"
                  ? fileRefs.bankStatements[index]
                  : fileRefs[key]
                ).current.click()
              }
            >
              Upload <i className="fa fa-cloud-upload"></i>
            </div>
            {/* Add button next to upload for bank statement 1 */}
            {key === "bankStatements" && index === 0 && (
              <div className="uploadbtnSize">
                <div className="uplodBtns add-more" onClick={addBankStatement}>
                  <i className="fa fa-plus"></i>
                </div>
              </div>
            )}
            {key === "bankStatements" && index > 0 && (
              <div
                className="uplodBtns removeBtn"
                onClick={() => removeFile(key, index)}
              >
                <i className="fa fa-trash"></i>
              </div>
            )}
          </div>
        </div>
        {passwordProtected && (
          <div className="form-group loan-in" style={{ color: "#1962cd" }}>
            <label>{label} Password</label>
            <input
              type="text"
              className={passwordError ? "salary-input error" : "salary-input"}
              placeholder={`Enter ${label} Password`}
              value={password}
              onChange={(e) => handlePasswordChange(key, index, e.target.value)}
              style={{ borderBottomColor: "#1962cd" }}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className="col-lg-6 col-md-12"
      style={{
        display:
          activeCard === "upload-document" && userType === "salaried"
            ? "block"
            : "none",
      }}
    >
      <h4 className="top-heading">Upload Documents</h4>
      <div className="form-group label">
        <div className="uploadsections p-20">
          {renderFileUpload("panCard", "Upload Pan Card", false, false, 0)}
          {renderFileUpload(
            "aadhaarFront",
            "Upload Aadhaar Front",
            false,
            false,
            0
          )}
          {!files.aadhaarFront.passwordProtected &&
            renderFileUpload(
              "aadhaarBack",
              "Upload Aadhaar Back",
              false,
              false,
              0
            )}
          {renderFileUpload(
            "salarySlip1",
            "Upload 1st Month Salary Slip",
            true,
            true,
            0
          )}
          {renderFileUpload(
            "salarySlip2",
            "Upload 2nd Month Salary Slip",
            true,
            true,
            0
          )}
          {renderFileUpload(
            "salarySlip3",
            "Upload 3rd Month Salary Slip",
            true,
            true,
            0
          )}
          {files.bankStatements.map((_, index) =>
            renderFileUpload(
              "bankStatements",
              `Upload Bank Statement ${index + 1}`,
              true,
              true,
              index
            )
          )}
          {renderFileUpload(
            "formSixteen",
            "Upload Current Year Form-16",
            true,
            true,
            0
          )}
          <div>
            <button
              className="btn btn-login"
              onClick={handleUploadDocument}
              disabled={isDisableBtn}
            >
              {isDisableBtn ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  Please Wait...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
